import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spin } from "antd";
import {
    BarChartOutlined,
    FundOutlined,
    LogoutOutlined,
    SettingOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { useUserState } from "../../common/UserContext";
import {
    useBudgetDemoFlag,
    useChatDemoFlag,
    useDemoMakerFlag,
    useIhgBriefsDemoFlag,
    useReportingDemoFlag,
    useUsageDemoFlag,
} from "../demo/demoFlags";
import { useAppNav } from "../../common/AppNavContext";
import { isAdmin, isSuperAdmin } from "../../types/UserGroup";
import { geturl } from "../../common/api/SafeClient";
import { stopImpersonating } from "../../common/api/AdminDashboardClient";
import { SvgIcon } from "../../common/components/SvgIcon";
import { usePageTracking } from "../../common/GoogleAnalytics/ReactRouter";
import { getNotificationStatus, markNotificationRead } from "../../common/api/NotificationsClient";
import { handleError } from "../../common/ErrorHandling";
import { UserNotification } from "../../types/NotificationTypes";
import { NotificationsPanel } from "../notifications/NotificationsPanel";
import { NotificationToasts } from "../notifications/NotificationToasts";
import { USAGE_PIPELINE_PATH, USAGE_PORTFOLIO_PATH } from "../usage/demo/paths";
import { useNextMovesCount } from "../usage/demo/model/hooks";
import AppShell, {
    AppShellMenuItem,
    NavBadge,
    NavPopoverAction,
    NavPopoverSubmenu,
    NavPopoverToggle,
    bottomNavGroup,
    navGroup,
    navSection,
    useNavPopovers,
} from "./AppShell";
import classes from "./AppLayout.module.less";

// How often the nav asks for new notifications while the tab is visible.
const NOTIFICATIONS_POLL_MS = 30_000;

export enum NavKey {
    Dashboard = "dashboard",
    Vendors = "vendors",
    Negotiations = "negotiations",
    BuyerBriefs = "buyerBriefs",
    Tactics = "tactics",
    Notifications = "notifications",
    Users = "users",
    Preferences = "preferences",
    SuperAdminMenu = "superAdminMenu",
    Projects = "projects",
    Budget = "budget",
    Reporting = "reporting",
    UsagePipeline = "usagePipeline",
    UsagePortfolio = "usagePortfolio",
}

export const WithActiveNav: React.FC<{ navKey: NavKey }> = ({ navKey }) => {
    const { setActiveNav } = useAppNav();

    useEffect(() => {
        setActiveNav((pre) => pre.concat(navKey));

        return () => {
            setActiveNav((pre) => pre.filter((k) => k !== navKey));
        };
    }, [setActiveNav, navKey]);

    return <Outlet />;
};

// The Oasis app frame: the shared AppShell holding the product nav. Entries
// are gated by role and by the demo flags; the account rows sit at the bottom.
const AppLayout: React.FC = () => {
    usePageTracking();

    const navigate = useNavigate();
    const { me } = useUserState();
    const [budgetEnabled, setBudgetEnabled] = useBudgetDemoFlag();
    const [chatDemoEnabled, setChatDemoEnabled] = useChatDemoFlag();
    const [reportingEnabled, setReportingEnabled] = useReportingDemoFlag();
    const [ihgBriefsEnabled, setIhgBriefsEnabled] = useIhgBriefsDemoFlag();
    const [demoMakerEnabled, setDemoMakerEnabled] = useDemoMakerFlag();
    const [usageEnabled, setUsageEnabled] = useUsageDemoFlag();
    const nextMoves = useNextMovesCount();
    const { highlightedNav, activeNav } = useAppNav();
    const { popoverNavItem, closePopovers } = useNavPopovers();

    // Notifications: the panel, the count on the nav entry, and a card per
    // notification that arrives while the app is open. The status is polled
    // from the newest id seen so far, so only what came after it is new.
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [toasts, setToasts] = useState<UserNotification[]>([]);
    const newestSeen = useRef<number>();

    const refreshNotifications = useCallback(async () => {
        try {
            const status = await getNotificationStatus(newestSeen.current);
            setUnreadCount(status.unread);
            // No notification yet is a watermark too (0): the first one to
            // arrive is new, and shows as a card like any other.
            newestSeen.current = status.latestId ?? 0;
            const fresh = status.fresh.filter((n) => n.readDate == null);
            if (fresh.length > 0) {
                setToasts((current) => [
                    ...fresh.filter((n) => !current.some((c) => c.id === n.id)),
                    ...current,
                ]);
            }
        } catch {
            // The next poll tries again.
        }
    }, []);

    // On arrival and on every page change, then every half minute while the
    // tab is visible, and as soon as it becomes visible again.
    const { pathname, search } = useLocation();
    useEffect(() => {
        void refreshNotifications();
    }, [pathname, refreshNotifications]);

    // The link in an email names its notification (see EmailRenderer): the
    // user opened it, so it is read now that they are in. The parameter
    // goes, so a reload or a bookmark does not mark it again.
    useEffect(() => {
        const params = new URLSearchParams(search);
        const id = Number(params.get("notification"));
        if (!id) return;
        params.delete("notification");
        navigate({ pathname, search: params.toString() }, { replace: true });
        markNotificationRead(id).then(refreshNotifications, handleError);
    }, [pathname, search, navigate, refreshNotifications]);

    useEffect(() => {
        const tick = () => {
            if (document.visibilityState === "visible") void refreshNotifications();
        };
        const timer = window.setInterval(tick, NOTIFICATIONS_POLL_MS);
        document.addEventListener("visibilitychange", tick);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [refreshNotifications]);

    const dropToast = (id: number) => setToasts((current) => current.filter((t) => t.id !== id));

    // A card's body goes to the project; that reads the notification.
    const openFromToast = (notification: UserNotification) => {
        dropToast(notification.id);
        setNotificationsOpen(false);
        markNotificationRead(notification.id).then(refreshNotifications, handleError);
    };
    const isAdminUser = isAdmin(me.roles || []);
    const isSuperAdminUser = isSuperAdmin(me.roles || []);

    const goTo = (path: string) => {
        closePopovers();
        navigate(path);
    };

    const exitImpersonation = async () => {
        try {
            await stopImpersonating();
        } finally {
            // A full navigation, not a state update: the session user just
            // changed, so every piece of loaded state is stale.
            window.location.assign("/");
        }
    };

    // A nav entry that navigates. The user guide highlights the entry it is
    // talking about.
    const navItem = (
        key: NavKey,
        icon: React.ReactNode,
        label: React.ReactNode,
        path: string
    ): AppShellMenuItem => ({
        key,
        icon,
        label,
        className: clsx(highlightedNav.includes(key) && classes.highlightedNav),
        onClick: () => navigate(path),
    });

    const mainItems: AppShellMenuItem[] = [
              navItem(NavKey.Negotiations, <SvgIcon name="group" />, "Top Opportunities", "/"),
              navItem(NavKey.Projects, <SvgIcon name="forms" />, "Procurement Hub", "/project"),
              navItem(NavKey.Vendors, <SvgIcon name="dataset" />, "Vendors", "/vendor"),
              navItem(NavKey.BuyerBriefs, <SvgIcon name="briefs" />, "Buyer Briefs", ihgBriefsEnabled ? "/superadmin/demo/ihg-brief" : "/briefs"),
              budgetEnabled &&
                  navItem(
                      NavKey.Budget,
                      <FundOutlined />,
                      "Financial Planning",
                      "/superadmin/demo/budget"
                  ),
              reportingEnabled &&
                  navItem(
                      NavKey.Reporting,
                      <BarChartOutlined />,
                      "Reporting",
                      "/superadmin/demo/reports"
                  ),
          ].filter((item): item is AppShellMenuItem => Boolean(item));

    // The Usage module, a section of its own under the main entries while its
    // demo flag is on.
    const usageSection = navSection("usage", "Usage", [
        navItem(
            NavKey.UsagePipeline,
            <SvgIcon name="progress" />,
            <>
                Pipeline
                <NavBadge count={nextMoves} />
            </>,
            USAGE_PIPELINE_PATH
        ),
        navItem(NavKey.UsagePortfolio, <SvgIcon name="graphs" />, "Portfolio", USAGE_PORTFOLIO_PATH),
    ]);

    // Notifications is for every user and opens a panel over the page rather
    // than a page of its own; it reads as selected while the panel is open.
    // The entries after it are for admins (plans/notifications.md, section 5).
    const notificationsItem: AppShellMenuItem = {
        key: NavKey.Notifications,
        icon: <SvgIcon name="notifications" />,
        label: (
            <>
                Notifications
                <NavBadge count={unreadCount} />
            </>
        ),
        onClick: () => setNotificationsOpen((open) => !open),
    };

    const managementGroup = bottomNavGroup(
        "management",
        [
            notificationsItem,
            isAdminUser &&
                navItem(NavKey.Users, <SvgIcon name="users" />, "Members", "/admin/members"),
        ].filter((item): item is AppShellMenuItem => Boolean(item))
    );

    const superAdminItem = popoverNavItem(
        NavKey.SuperAdminMenu,
        <SvgIcon name="admin" />,
        "Super Admin",
        <>
            <NavPopoverAction onClick={() => goTo("/superadmin/impersonate")}>
                Impersonate
            </NavPopoverAction>
            <NavPopoverSubmenu label="Demo">
                <NavPopoverAction onClick={() => goTo("/superadmin/demo/tail-spend")}>
                    Tail Spend
                </NavPopoverAction>
                <NavPopoverToggle checked={ihgBriefsEnabled} onChange={setIhgBriefsEnabled}>
                    IHG Custom Buyer Briefs
                </NavPopoverToggle>
                <NavPopoverToggle checked={budgetEnabled} onChange={setBudgetEnabled}>
                    Budget
                </NavPopoverToggle>
                <NavPopoverToggle checked={chatDemoEnabled} onChange={setChatDemoEnabled}>
                    Chat
                </NavPopoverToggle>
                <NavPopoverToggle checked={reportingEnabled} onChange={setReportingEnabled}>
                    Reporting
                </NavPopoverToggle>
                <NavPopoverToggle checked={demoMakerEnabled} onChange={setDemoMakerEnabled}>
                    Demo Maker
                </NavPopoverToggle>
                <NavPopoverToggle checked={usageEnabled} onChange={setUsageEnabled}>
                    Usage
                </NavPopoverToggle>
            </NavPopoverSubmenu>
            <NavPopoverAction onClick={() => goTo("/superadmin/dashboard")}>
                Dashboard
            </NavPopoverAction>
        </>
    );

    const fullName = [me.firstName, me.lastName].filter(Boolean).join(" ") || me.name;
    // While impersonating, the entry names the org being acted as, not the bot user.
    const impersonatedOrg =
        me.impersonating?.organizationName || me.impersonating?.organizationDomain;

    const profileItem = popoverNavItem(
        NavKey.Preferences,
        me.impersonating ? <UserSwitchOutlined /> : <SvgIcon name="user" />,
        me.impersonating ? `Bot: ${impersonatedOrg}` : fullName,
        <>
            <NavPopoverAction icon={<SettingOutlined />} onClick={() => goTo("/preferences")}>
                My Account
            </NavPopoverAction>
            {me.impersonating && (
                <NavPopoverAction icon={<UserSwitchOutlined />} onClick={exitImpersonation}>
                    Exit impersonation
                </NavPopoverAction>
            )}
            <NavPopoverAction
                icon={<LogoutOutlined />}
                // A full navigation rather than a route change: the API clears the
                // session and redirects to the login page.
                onClick={() => window.location.assign(geturl("/api/logout"))}
            >
                Log out
            </NavPopoverAction>
        </>
    );

    const accountItems: AppShellMenuItem[] = [
        isSuperAdminUser && superAdminItem,
        profileItem,
    ].filter((item): item is AppShellMenuItem => Boolean(item));

    return (
        <Spin spinning={me.email === undefined}>
            <AppShell
                logo={
                    <Link to="/dashboard">
                        <img src="/infinity-loop-logo-nav.svg" alt="Infinity Loop" />
                    </Link>
                }
                compactLogo={
                    <Link to="/dashboard">
                        <img src="/infinity-loop-logo-nav-compact.svg" alt="Infinity Loop" />
                    </Link>
                }
                menuItems={[
                    navGroup("main", mainItems),
                    ...(usageEnabled ? [usageSection] : []),
                    managementGroup,
                    bottomNavGroup("account", accountItems),
                ]}
                selectedKeys={notificationsOpen ? [...activeNav, NavKey.Notifications] : activeNav}
                defaultOpenKeys={["usage"]}
            >
                <div className={classes.content}>
                    <Outlet />
                </div>
                <>
                    <NotificationsPanel
                        open={notificationsOpen}
                        onClose={() => setNotificationsOpen(false)}
                        unreadCount={unreadCount}
                        onRead={(id) => {
                            dropToast(id);
                            void refreshNotifications();
                        }}
                        onReadAll={() => {
                            setToasts([]);
                            setUnreadCount(0);
                        }}
                    />
                    <NotificationToasts
                        toasts={toasts}
                        onOpen={openFromToast}
                        onDismiss={dropToast}
                    />
                </>
            </AppShell>
        </Spin>
    );
};

export default AppLayout;
